import { Controller } from "@hotwired/stimulus"
import { Haptics } from "../lib/haptics"
import { syncGateReason } from "../lib/gate_reason"

// A11Y-1 / BUTTON-4 (#13355): every disabled frame of Checkout and Clear — and
// of Apply while this agreement locks the coupon field — says which gate holds
// it, as the button's own gl-tooltip plus a "<label> — <reason>" accessible
// name (lib/gate_reason), withdrawn the moment the gate opens. The gates are
// unchanged: each `ready` below is exactly the old `disabled` negated.
export default class extends Controller {
  // The reasons are authored ONCE, in carts/show, which paints the first-frame
  // reason server-side and stamps each one here as a value, so the first paint
  // and every later gate change say the same thing. The defaults only serve a
  // DOM that stamps nothing (the Jest fixtures).
  static values = {
    csrfToken: String,
    cartHasMinLeads: Boolean,
    managerCompedCheckoutAvailable: Boolean,
    userLicensedStates: Array,
    agreeReason: { type: String, default: "Check the agreement box first" },
    acknowledgeReason: { type: String, default: "Check every acknowledgment first" },
    statesReason: { type: String, default: "Confirm your states are correct first" },
    nameReason: { type: String, default: "Type your full legal name first" },
    nameFixReason: { type: String, default: "Fix your typed legal name first" },
    signReason: { type: String, default: "Sign above first" },
    clearReason: { type: String, default: "No signature to clear yet" },
    minimumReason: { type: String, default: "Add leads to reach the lead minimum" },
    submittingReason: { type: String, default: "Submitting your order…" }
  }

  connect() {
    // The double-submit latch starts open; an explicit false, not undefined.
    this.checkoutSubmitted = false
    this.initializeElements()
    this.setupEventListeners()
    this.updateGatedElements()
    this.updateCheckoutButton()
    this.updateAcknowledgmentHighlights()
    this.syncHiddenFields()
    this.updateSelectAllAck()
  }

  disconnect() {
    if (this.reviewPolicyLink && this.reviewPolicyClickHandler) {
      this.reviewPolicyLink.removeEventListener('click', this.reviewPolicyClickHandler)
      this.reviewPolicyLink.removeEventListener('auxclick', this.reviewPolicyAuxclickHandler)
    }
    if (this.agreeToPolicy && this.agreeToPolicyChangeHandler) {
      this.agreeToPolicy.removeEventListener('change', this.agreeToPolicyChangeHandler)
    }
    if (this.autoCopyStates && this.autoCopyStatesChangeHandler) {
      this.autoCopyStates.removeEventListener('change', this.autoCopyStatesChangeHandler)
    }
    if (this.typedNameInput && this.typedNameInputHandler) {
      this.typedNameInput.removeEventListener('input', this.typedNameInputHandler)
    }
    if (this.statesConfirm && this.statesConfirmChangeHandler) {
      this.statesConfirm.removeEventListener('change', this.statesConfirmChangeHandler)
    }
    if (this.checkoutButton && this.checkoutClickHandler) {
      this.checkoutButton.removeEventListener('click', this.checkoutClickHandler)
    }
    if (this.checkoutForm && this.checkoutSubmitHandler) {
      this.checkoutForm.removeEventListener('submit', this.checkoutSubmitHandler)
    }
    if (this.confirmAutoCopyBtn && this.confirmAutoCopyClickHandler) {
      this.confirmAutoCopyBtn.removeEventListener('click', this.confirmAutoCopyClickHandler)
    }
    if (this.managerCompedCheckoutOk && this.managerCompedCheckoutOkHandler) {
      this.managerCompedCheckoutOk.removeEventListener('click', this.managerCompedCheckoutOkHandler)
    }
    if (this.policyAcknowledgments && this.acknowledgmentChangeHandler) {
      this.policyAcknowledgments.forEach((cb) => {
        cb.removeEventListener('change', this.acknowledgmentChangeHandler)
      })
    }
    if (this.selectAllAck && this.selectAllAckHandler) {
      this.selectAllAck.removeEventListener('change', this.selectAllAckHandler)
    }
  }

  initializeElements() {
    // The pad itself is the shared `signature-pad` controller (audit F14 —
    // checkout was the last screen hand-rolling raw canvas drawing). The cart
    // keeps only the element handles it gates: the canvas (pointer-events /
    // opacity), the Clear button (disabled state), and the two posted fields,
    // which it clears defensively.
    this.canvas = document.getElementById('signature_canvas')
    this.signaturePadElement = document.querySelector('[data-controller~="signature-pad"]')
    this.clearSignatureBtn = document.getElementById('clear-signature')
    this.signatureData = document.getElementById('signature_data')
    this.hiddenSignatureData = document.getElementById('hidden_signature_data')
    this.agreeToPolicy = document.getElementById('agree_to_policy')
    // #11688: the typed legal name is required server-side, so the button gates
    // on it too — the modal confirm flows call form.submit(), which skips the
    // input's own HTML5 `required` check.
    this.typedNameInput = document.getElementById('typed_name')
    // #15409: "These states are correct" — its own gate, outside the policy
    // acknowledgments so "Select all" never ticks it.
    this.statesConfirm = document.getElementById('confirm_states_correct')
    this.typedNameHint = document.getElementById('typed_name_hint')
    this.reviewPolicyLink = document.getElementById('review-policy-link-cart')
    // #11726: set to '1' when the buyer opens the full policy; CartCheckoutProcessor
    // stamps agreed_policy_viewed_at server-side from this flag (never a client time).
    this.hiddenPolicyViewed = document.getElementById('hidden_policy_viewed')
    this.autoCopyStates = document.getElementById('auto_copy_states')
    this.checkoutButton = document.getElementById('btn-checkout')
    this.hiddenAgreeToPolicy = document.getElementById('hidden_agree_to_policy')
    this.checkoutForm = document.getElementById('checkout-form')
    // #21066 diagnostics: which path posted the form, logged on a server refusal.
    this.signatureSubmitPath = document.getElementById('signature_submit_path')
    this.confirmAutoCopyBtn = document.getElementById('confirm-auto-copy')
    this.selectAllAck = document.getElementById('ack_select_all')
    this.hiddenMarkPaidForAgent = document.getElementById('hidden_mark_paid_for_agent')
    this.hiddenMarkPaidTreatAsFree = document.getElementById('hidden_mark_paid_treat_as_free')
    this.managerCompedCheckoutModal = document.getElementById('managerCompedCheckoutModal')
    this.managerCompedCheckoutOk = document.getElementById('manager-comped-checkout-ok')
    this.managerCompedNotFreeRadio = document.getElementById('manager-comped-not-free')

    this.couponInput = document.getElementById('coupon_code_input')
    this.couponApplyBtn = document.getElementById('apply_coupon_btn')

    this.policyAcknowledgments = Array.from(document.querySelectorAll('.js-policy-acknowledgment'))
    this.acknowledgmentHiddenMirrors = this.policyAcknowledgments
      .map((cb) => ({ checkbox: cb, hidden: document.getElementById(`hidden_${cb.id}`) }))
      .filter((pair) => pair.hidden)

    this.hasSignature = false
    this.minLeadsToastShown = false
    this.managerCompedCheckoutChoiceMade = false
  }

  setupEventListeners() {
    this.reviewPolicyClickHandler = () => {
      if (this.agreeToPolicy) {
        this.agreeToPolicy.disabled = false
      }
      if (this.hiddenPolicyViewed) this.hiddenPolicyViewed.value = '1'
    }
    // auxclick fires for EVERY non-primary button — right-click, and on some
    // mice the side back/forward buttons — none of which open the policy. Only
    // a middle-click (button 1, open in a background tab) is a real view, and
    // this flag becomes evidence on the Certificate of Acceptance (#11726).
    this.reviewPolicyAuxclickHandler = (event) => {
      if (event.button === 1) this.reviewPolicyClickHandler()
    }
    this.agreeToPolicyChangeHandler = () => {
      // The master checkbox does NOT propagate to the individual
      // acknowledgments (#9811): the Stripe rebuttal letter's claim that the
      // customer "affirmatively checked all of the following" holds only if
      // each one is checked on its own. Each acknowledgment is visible and
      // checked by itself; this handler only reflects the master's own state.
      // An applied coupon SURVIVES an untick: the cart remembers it
      // server-side and checkout re-validates it, so there is no stale
      // client-only discount to purge.
      this.updateGatedElements()
      this.updateCheckoutButton()
      this.updateAcknowledgmentHighlights()
      this.syncHiddenFields()
    }

    this.autoCopyStatesChangeHandler = () => {
      this.syncHiddenFields()
    }
    this.typedNameInputHandler = () => this.updateCheckoutButton()
    this.statesConfirmChangeHandler = () => this.updateCheckoutButton()
    this.checkoutClickHandler = this.handleCheckoutClick.bind(this)
    this.checkoutSubmitHandler = this.handleCheckoutSubmit.bind(this)
    this.confirmAutoCopyClickHandler = () => this.confirmAutoCopy()
    this.managerCompedCheckoutOkHandler = () => this.confirmManagerCompedCheckout()

    this.reviewPolicyLink?.addEventListener('click', this.reviewPolicyClickHandler)
    // A middle-click opens the policy in a background tab and fires auxclick,
    // not click (#11726 review) — the view is just as real. Middle button only.
    this.reviewPolicyLink?.addEventListener('auxclick', this.reviewPolicyAuxclickHandler)
    this.agreeToPolicy?.addEventListener('change', this.agreeToPolicyChangeHandler)
    this.typedNameInput?.addEventListener('input', this.typedNameInputHandler)
    this.statesConfirm?.addEventListener('change', this.statesConfirmChangeHandler)
    this.autoCopyStates?.addEventListener('change', this.autoCopyStatesChangeHandler)
    this.checkoutButton?.addEventListener('click', this.checkoutClickHandler)
    this.checkoutForm?.addEventListener('submit', this.checkoutSubmitHandler)
    this.confirmAutoCopyBtn?.addEventListener('click', this.confirmAutoCopyClickHandler)
    this.managerCompedCheckoutOk?.addEventListener('click', this.managerCompedCheckoutOkHandler)

    // The SINGLE change handler for the six acknowledgments (#9811 made them
    // individually required rather than cascaded from the master). It does not
    // call updateGatedElements(): that gates the signature canvas on the
    // MASTER checkbox only, so an ack toggling would make it a no-op.
    this.acknowledgmentChangeHandler = () => {
      this.updateCheckoutButton()
      this.updateAcknowledgmentHighlights()
      this.syncHiddenFields()
      this.updateSelectAllAck()
    }
    this.policyAcknowledgments?.forEach((cb) => {
      cb.addEventListener('change', this.acknowledgmentChangeHandler)
    })

    this.selectAllAckHandler = (e) => {
      const checked = e.target.checked
      this.policyAcknowledgments?.forEach((cb) => {
        cb.checked = checked
      })
      this.updateCheckoutButton()
      this.updateAcknowledgmentHighlights()
      this.syncHiddenFields()
      this.updateSelectAllAck()
    }
    this.selectAllAck?.addEventListener('change', this.selectAllAckHandler)
  }

  updateSelectAllAck() {
    if (!this.selectAllAck || !this.policyAcknowledgments?.length) return
    const checkedCount = this.policyAcknowledgments.filter((cb) => cb.checked).length
    const total = this.policyAcknowledgments.length
    this.selectAllAck.checked = checkedCount === total
    this.selectAllAck.indeterminate = checkedCount > 0 && checkedCount < total
  }

  // `signature-pad:change` — the shared pad controller announces its own state
  // after every finished stroke and on clear, and has ALREADY mirrored the PNG
  // data URL into both posted fields (they are its `input` targets). The cart
  // only tracks presence, which is what gates Checkout and Clear.
  signatureChanged(event) {
    this.hasSignature = !event.detail.empty
    this.updateClearSignatureButton()
    this.updateCheckoutButton()
  }

  // Only enabled when policy agreed AND a signature exists. A disabled Clear
  // names the agreement first (the pad is locked until it is ticked), then the
  // missing signature.
  updateClearSignatureButton() {
    if (!this.clearSignatureBtn) return
    const agreed = this.agreeToPolicy?.checked || false
    syncGateReason(this.clearSignatureBtn, {
      ready: agreed && this.hasSignature,
      reason: agreed ? this.clearReasonValue : this.agreeReasonValue
    })
  }

  signaturePadController() {
    if (!this.signaturePadElement) return null
    return this.application?.getControllerForElementAndIdentifier(this.signaturePadElement, 'signature-pad')
  }

  // Unchecking the master agreement discards the signature. Delegates the
  // canvas + input reset to the shared pad controller (whose clear() also
  // announces signature-pad:change). Belt-and-suspenders: the two posted fields
  // are cleared directly as well, so an unmounted pad controller can never
  // leave a stale signature in the payload.
  clearSignature() {
    this.signaturePadController()?.clear()
    this.hasSignature = false
    if (this.signatureData) this.signatureData.value = ''
    if (this.hiddenSignatureData) this.hiddenSignatureData.value = ''
    this.updateClearSignatureButton()
    this.updateCheckoutButton()
  }

  updateGatedElements() {
    const agreed = this.agreeToPolicy?.checked || false

    // Signature canvas
    if (this.canvas) {
      this.canvas.style.pointerEvents = agreed ? '' : 'none'
      this.canvas.style.opacity = agreed ? '' : '0.5'
    }

    this.updateClearSignatureButton()

    // Clear signature when unchecking
    if (!agreed && this.hasSignature) {
      this.clearSignature()
    }

    // Coupon input — an APPLIED coupon keeps its field locked whatever the
    // agreement does (coupon_code_controller marks it data-coupon-applied);
    // only Remove reopens it. Otherwise the field follows the agreement.
    if (this.couponInput && this.couponInput.dataset.couponApplied !== 'true') {
      this.couponInput.disabled = !agreed
      if (agreed) {
        this.couponInput.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }

    // Coupon apply button — force disable when unchecked. The field it serves
    // is locked by this agreement, so that is the reason Apply gives — unless a
    // coupon is applied: coupon_code_controller already holds that Apply with
    // its own "remove it first" reason, which is left in place.
    if (this.couponApplyBtn && !agreed) {
      if (this.couponInput?.dataset.couponApplied === 'true') {
        this.couponApplyBtn.disabled = true
      } else {
        syncGateReason(this.couponApplyBtn, { ready: false, reason: this.agreeReasonValue })
      }
    }

  }

  allAcknowledgmentsChecked() {
    // Fail-closed: server requires every POLICY_ACKNOWLEDGMENT_PARAM. If the
    // visible checkboxes are absent, keep checkout disabled instead of letting
    // the user click into a server-side rejection.
    if (!this.policyAcknowledgments || this.policyAcknowledgments.length === 0) return false
    return this.policyAcknowledgments.every((cb) => cb.checked)
  }

  updateAcknowledgmentHighlights() {
    if (!this.policyAcknowledgments) return
    const masterChecked = this.agreeToPolicy?.checked || false
    this.policyAcknowledgments.forEach((cb) => {
      const wrapper = cb.closest('.gl-form-check')
      const shouldHighlight = masterChecked && !cb.checked
      if (wrapper) wrapper.classList.toggle('is-required-attention', shouldHighlight)
      if (shouldHighlight) {
        cb.setAttribute('aria-invalid', 'true')
      } else {
        cb.removeAttribute('aria-invalid')
      }
    })
  }

  // Mirrors CartCheckoutProcessor#identity_capture_present?: whitespace is not a name.
  hasTypedName() {
    return (this.typedNameInput?.value || '').trim() !== ''
  }

  // Mirrors the character and two-word rules of TypedLegalName.valid? (#13732)
  // so a name the server would refuse is flagged beside the field before the
  // handoff — an alert after a redirect reads to a buyer mid-checkout as
  // "nothing happened". Deliberately no stricter
  // than the server: letters in any script, apostrophes, hyphens, periods and
  // spaces, at least two words each carrying a letter. Whitespace is squished
  // first, exactly as TypedLegalName.normalize does, so a pasted no-break space
  // is a word gap on both sides rather than a character the client alone refuses.
  typedNamePlausible() {
    const value = (this.typedNameInput?.value || '').replace(/\s+/g, ' ').trim()
    if (value === '') return true
    if (!/^[\p{L}\p{M}'’ʼ.\- ]+$/u.test(value)) return false
    const words = value.split(/\s+/).filter((word) => /\p{L}/u.test(word))
    return words.length >= 2
  }

  updateTypedNameHint() {
    const plausible = this.typedNamePlausible()
    if (this.typedNameInput) {
      if (plausible) {
        this.typedNameInput.removeAttribute('aria-invalid')
      } else {
        this.typedNameInput.setAttribute('aria-invalid', 'true')
      }
    }
    this.typedNameHint?.classList.toggle('d-none', plausible)
  }

  // No box is rendered for a cart with no stated items, so absence passes; a
  // rendered box must be ticked.
  statesConfirmed() {
    return !this.statesConfirm || this.statesConfirm.checked
  }

  updateCheckoutButton() {
    this.updateTypedNameHint()
    const agreeChecked = this.agreeToPolicy?.checked || false
    const ackChecked = this.allAcknowledgmentsChecked()
    const gatesMet = agreeChecked && ackChecked && this.hasSignature && this.hasTypedName() &&
      this.typedNamePlausible() && this.statesConfirmed()
    const shouldEnable = gatesMet && this.cartHasMinLeadsValue

    if (this.checkoutButton) {
      syncGateReason(this.checkoutButton, { ready: shouldEnable, reason: this.checkoutHoldReason() })
    }

    if (gatesMet && !this.cartHasMinLeadsValue && !this.minLeadsToastShown) {
      this.minLeadsToastShown = true
      this.showToast('You need to add more leads to achieve the minimum count.', 'bg-warning')
    }
  }

  // Why Checkout is held, in the order the buyer meets the gates on the page —
  // except the lead minimum, a property of the cart no box on this form can
  // satisfy, which speaks first (the "Minimum not met" alert above says the
  // same). It reads the gate's own predicates and decides nothing.
  checkoutHoldReason() {
    if (!this.cartHasMinLeadsValue) return this.minimumReasonValue
    if (!(this.agreeToPolicy?.checked || false)) return this.agreeReasonValue
    if (!this.allAcknowledgmentsChecked()) return this.acknowledgeReasonValue
    if (!this.hasTypedName()) return this.nameReasonValue
    if (!this.typedNamePlausible()) return this.nameFixReasonValue
    if (!this.statesConfirmed()) return this.statesReasonValue
    return this.signReasonValue
  }

  syncHiddenFields() {
    if (this.agreeToPolicy && this.hiddenAgreeToPolicy) {
      this.hiddenAgreeToPolicy.value = this.agreeToPolicy.checked ? '1' : ''
    }
    if (this.acknowledgmentHiddenMirrors) {
      this.acknowledgmentHiddenMirrors.forEach(({ checkbox, hidden }) => {
        hidden.value = checkbox.checked ? '1' : ''
      })
    }
  }

  handleCheckoutClick(e) {
    if (this.shouldShowManagerCompedCheckoutModal()) {
      e.preventDefault()
      this.showManagerCompedCheckoutModal()
      return
    }

    this.handleAutoCopyStates(e)
  }

  // The checkout form is turbo:false (external Stripe redirect), so Turbo's
  // automatic submit-button disabling does NOT apply — a double-click posts
  // checkout twice and runs two concurrent pipelines. Every submission path
  // funnels through this single latch.
  handleCheckoutSubmit(e) {
    if (this.checkoutSubmitted) {
      e.preventDefault()
      return
    }
    this.lockCheckoutSubmit()
    this.recordSubmitPath('button')

    // The shared pad's submit-time empty-pad re-check is ALSO a submit listener
    // on this form and is bound after this one (its controller sits on the inner
    // form element), so it can cancel a submission we have already latched. A
    // canceled submit never navigates, so releasing the latch here is what keeps
    // a refused submission recoverable instead of wedging checkout until reload.
    // The microtask runs after every submit listener, so defaultPrevented is
    // final by then; a successful submit leaves it false and stays latched.
    queueMicrotask(() => {
      if (e.defaultPrevented) this.unlockCheckoutSubmit()
    })
  }

  lockCheckoutSubmit() {
    this.checkoutSubmitted = true
    if (this.checkoutButton) syncGateReason(this.checkoutButton, { ready: false, reason: this.submittingReasonValue })
  }

  // Re-derives the button state from the real gates rather than blindly
  // enabling it — a released latch must not hand back a Checkout button the
  // agreement/acknowledgment/signature gates would have kept disabled.
  unlockCheckoutSubmit() {
    this.checkoutSubmitted = false
    this.updateCheckoutButton()
  }

  // Programmatic submits (modal confirmations) bypass the 'submit' event, so
  // they latch explicitly before calling form.submit() — and, for the same
  // reason, they never reach `submit->signature-pad#submit`. Without running
  // the pad's check here they posted the last endStroke export unchecked, and
  // Mac Safari buyers who had signed were bounced as blank_pad (#21066). A
  // refusal leaves the latch open: the pad has already told the buyer to sign,
  // and its change event re-derives the Checkout button.
  submitCheckoutForm() {
    if (!this.checkoutForm || this.checkoutSubmitted) return
    if (!this.signatureReadyForSubmit()) return

    this.lockCheckoutSubmit()
    this.recordSubmitPath('modal')
    this.checkoutForm.submit()
  }

  // The pad's own submit-time check and fresh export. No pad controller means
  // nothing can vouch for the signature, so refuse here rather than post a
  // form the server will bounce without a word to the buyer.
  signatureReadyForSubmit() {
    const pad = this.signaturePadController()
    if (pad) return pad.prepareSubmit()

    window.showAlertModal('Please draw your signature before submitting.')
    return false
  }

  recordSubmitPath(path) {
    if (this.signatureSubmitPath) this.signatureSubmitPath.value = path
  }

  handleAutoCopyStates(e) {
    if (!this.shouldHandleAutoCopyStatesCheckout()) return

    e.preventDefault()
    this.showAutoCopyStatesCheckoutModal()
  }

  shouldShowManagerCompedCheckoutModal() {
    return Boolean(this.managerCompedCheckoutAvailableValue &&
      !this.managerCompedCheckoutChoiceMade &&
      this.managerCompedCheckoutModal)
  }

  shouldHandleAutoCopyStatesCheckout() {
    return this.autoCopyStates?.checked
  }

  showAutoCopyStatesCheckoutModal() {
    if (this.userLicensedStatesValue.length === 0) {
      this.showNoLicensedStatesModal()
    } else {
      this.showAutoCopyStatesModal()
    }
  }

  showNoLicensedStatesModal() {
    const modal = new bootstrap.Modal(document.getElementById('noLicensedStatesModal'))
    modal.show()
  }

  showAutoCopyStatesModal() {
    const statesList = this.userLicensedStatesValue.join(', ')
    const message = `Are you sure you want to add the following states from your profile if the order cannot be filled with the state in the order: ${statesList}`
    const modalMessage = document.getElementById('modal-message')
    if (modalMessage) modalMessage.textContent = message

    const modal = new bootstrap.Modal(document.getElementById('autoCopyStatesModal'))
    modal.show()
  }

  confirmAutoCopy() {
    Haptics.heavy() // Tactile feedback for checkout confirmation
    const modal = bootstrap.Modal.getInstance(document.getElementById('autoCopyStatesModal'))
    if (modal) modal.hide()
    this.submitCheckoutForm()
  }

  showManagerCompedCheckoutModal() {
    const modal = bootstrap.Modal.getOrCreateInstance(this.managerCompedCheckoutModal)
    modal.show()
  }

  hideManagerCompedCheckoutModal() {
    const modal = bootstrap.Modal.getInstance(this.managerCompedCheckoutModal)
    if (modal) modal.hide()
  }

  // OK handler. Always marks the order paid for the agent (this modal never goes
  // to Stripe). The "not free" radio flips mark_paid_treat_as_free to '0' so the
  // server records the order paid but leaves the free-leads column empty.
  // Cancel uses data-bs-dismiss and leaves managerCompedCheckoutChoiceMade false,
  // so a later Checkout click re-opens this modal instead of submitting.
  confirmManagerCompedCheckout() {
    Haptics.heavy()
    this.managerCompedCheckoutChoiceMade = true
    if (this.hiddenMarkPaidForAgent) this.hiddenMarkPaidForAgent.value = '1'
    if (this.hiddenMarkPaidTreatAsFree) {
      this.hiddenMarkPaidTreatAsFree.value = this.managerCompedNotFreeRadio?.checked ? '0' : '1'
    }
    this.hideManagerCompedCheckoutModal()
    this.submitCheckoutForm()
  }

  showToast(message, type = '') {
    if (window.showToast) {
      window.showToast(message, type)
    } else {
      console.log(message)
    }
  }
}
